/**
 * Eneto UTM Persistence
 * ---------------------
 * Loest das Problem, dass UTM-Parameter manuell von Link zu Link
 * weitergereicht werden muessen (Landingpage -> Pop-up -> Formular).
 *
 * FIX 2026-08-13 (UTM-Passthrough-Bug): Es gibt jetzt ZWEI GETRENNTE
 * SPEICHER statt einem einzigen 30-Tage-Speicher:
 *
 * 1. SESSION-SPEICHER (sessionStorage, "eneto_utm_session")
 *    Wird verwendet, um [data-utm-passthrough]-Elemente (Formulare UND
 *    Links, z.B. der Header-CTA-Button) aktiv mit UTM-Parametern zu
 *    befuellen. Lebt nur fuer die Dauer des Browser-Tabs. Kommt ein
 *    Nutzer Tage spaeter ohne neuen UTM-Parameter zurueck (neuer Tab /
 *    neue Session), ist dieser Speicher leer -> der Button "klebt" nicht
 *    mehr dauerhaft an einer alten Kampagne.
 *
 * 2. HINTERGRUND-SPEICHER (localStorage, "eneto_utm_attribution")
 *    Rein fuer spaetere Attribution / Reporting / Provisionsabrechnung
 *    gedacht (z.B. Auslesen bei Checkout/Conversion ueber
 *    EnetoUTM.getAttribution()). Laeuft nach 14 Tagen ab. Wird NIE aktiv
 *    verwendet, um Buttons/Links/Formulare zu befuellen - nur zum
 *    Nachschlagen "woher kam dieser Nutzer urspruenglich" ueber die
 *    Session hinaus.
 *
 * BEIDE Speicher folgen Last-Click: Sobald ein neuer utm_* Parameter in
 * der URL auftaucht, wird der bestehende Wert ueberschrieben (nicht nur
 * beim allerersten Besuch, wie zuvor). Reiner Direct-Traffic (keine neuen
 * UTM-Parameter in der URL, z.B. interne Navigation) loescht NICHTS und
 * verlaengert auch KEINE Gueltigkeit - der bestehende Wert bleibt einfach
 * unangetastet stehen, bis er entweder ueberschrieben wird (neuer
 * Touchpoint) oder ablaeuft (Hintergrundspeicher) bzw. die Session endet
 * (Session-Speicher).
 *
 * Seiten mit fest hinterlegtem UTM-Zweig (Infranken, Home&Smart, Svea)
 * nutzen in header.liquid eigene {% elsif request.path == ... %}-Zweige
 * OHNE data-utm-passthrough und werden von diesem Mechanismus nie
 * beruehrt.
 *
 * Nutzung im Theme:
 *   <script src="utm-persistence.js"></script>
 *   <form data-utm-passthrough> ... </form>
 *   <a data-utm-passthrough href="..."> ... </a>
 *
 * Das Script laeuft automatisch bei DOMContentLoaded und:
 *   1. liest utm_* aus der aktuellen URL
 *   2. ueberschreibt damit Session- UND Hintergrundspeicher (Last-Click),
 *      sofern neue Parameter vorhanden sind
 *   3. befuellt alle [data-utm-passthrough] Formulare/Links ausschliesslich
 *      aus dem SESSION-Speicher
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(); // Node / Tests
  } else {
    root.EnetoUTM = factory(); // Browser
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var BACKGROUND_KEY = 'eneto_utm_attribution';
  var SESSION_KEY = 'eneto_utm_session';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var BACKGROUND_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 Tage Gueltigkeit fuer Reporting/Attribution

  function parseIncomingUTM(search) {
    var params = new URLSearchParams(search || '');
    var result = {};
    UTM_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) result[key] = value;
    });
    return result;
  }

  function hasAny(obj) {
    return !!obj && Object.keys(obj).length > 0;
  }

  function safeGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSet(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (e) {
      /* storage nicht verfuegbar (z.B. Privacy-Mode) - fail silent */
    }
  }

  function safeRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch (e) { /* ignore */ }
  }

  function isExpired(data, now, maxAgeMs) {
    if (!data || !data.capturedAt) return true;
    return (now || Date.now()) - data.capturedAt > maxAgeMs;
  }

  // --- Hintergrundspeicher (localStorage, 14 Tage, nur fuer Reporting/Attribution) ---

  function readBackground(storage, now) {
    if (!storage) return null;
    try {
      var raw = safeGet(storage, BACKGROUND_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (isExpired(data, now, BACKGROUND_MAX_AGE_MS)) {
        safeRemove(storage, BACKGROUND_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeBackground(storage, incoming, href, now) {
    if (!storage) return;
    var data = { params: incoming, capturedAt: now, firstUrl: href || null };
    safeSet(storage, BACKGROUND_KEY, JSON.stringify(data));
  }

  // --- Session-Speicher (sessionStorage, aktiv fuer Passthrough-Elemente) ---

  function readSession(storage) {
    if (!storage) return null;
    try {
      var raw = safeGet(storage, SESSION_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      return data && hasAny(data.params) ? data : null;
    } catch (e) {
      return null;
    }
  }

  function writeSession(storage, incoming, href, now) {
    if (!storage) return;
    var data = { params: incoming, capturedAt: now, firstUrl: href || null };
    safeSet(storage, SESSION_KEY, JSON.stringify(data));
  }

  /**
   * Erfasst UTM-Parameter aus der aktuellen URL. Last-Click: ist ein neuer
   * utm_* Parameter in der URL vorhanden, ueberschreibt er IMMER den
   * bestehenden Wert (Session- UND Hintergrundspeicher). Reiner
   * Direct-Traffic (keine UTM-Parameter in der URL) fasst beide Speicher
   * nicht an - kein Loeschen, keine Laufzeit-Verlaengerung.
   */
  function capture(options) {
    options = options || {};
    var localStore = options.localStorage;
    var sessionStore = options.sessionStorage;
    var now = options.now || Date.now();
    var incoming = parseIncomingUTM(options.search);

    if (hasAny(incoming)) {
      writeBackground(localStore, incoming, options.href, now);
      writeSession(sessionStore, incoming, options.href, now);
      return incoming;
    }
    return null;
  }

  /**
   * Attribution fuer Reporting/Provisionsabrechnung (14 Tage,
   * sitzungsuebergreifend). Wird NICHT verwendet, um Buttons/Links/
   * Formulare zu befuellen - nur zum Nachschlagen der urspruenglichen
   * Herkunft (z.B. beim Checkout an GTM/dataLayer uebergeben).
   */
  function getAttribution(localStore, now) {
    var stored = readBackground(localStore, now);
    return (stored && stored.params) || {};
  }

  /**
   * Aktive Passthrough-Werte fuer die aktuelle Session (Landingpage ->
   * Pop-up -> Formular/Button). Leer, sobald die Session ohne neuen
   * UTM-Touchpoint beginnt (z.B. neuer Tab an einem anderen Tag).
   */
  function getSessionAttribution(sessionStore) {
    var stored = readSession(sessionStore);
    return (stored && stored.params) || {};
  }

  function clearAttribution(localStore) {
    safeRemove(localStore, BACKGROUND_KEY);
  }

  function clearSession(sessionStore) {
    safeRemove(sessionStore, SESSION_KEY);
  }

  function appendParamsToUrl(url, params, baseHref) {
    try {
      var base = baseHref || (typeof location !== 'undefined' ? location.href : undefined);
      var u = new URL(url, base);
      Object.keys(params).forEach(function (k) {
        u.searchParams.set(k, params[k]);
      });
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /**
   * Wendet die SESSION-Attribution auf alle [data-utm-passthrough]
   * Formulare UND Links einer Seite an. Formulare bekommen hidden inputs,
   * Links bekommen die Parameter an ihre href angehaengt. Ist die Session
   * leer (kein aktueller Touchpoint), passiert nichts - bestehende hrefs
   * bleiben unveraendert (das ist der Fix fuer das dauerhafte "Kleben"
   * alter UTM-Parameter am Header-CTA-Button).
   */
  function applyToDocument(doc, localStore, sessionStore, loc, now) {
    capture({ localStorage: localStore, sessionStorage: sessionStore, search: loc.search, href: loc.href, now: now });
    var attribution = getSessionAttribution(sessionStore);
    if (!hasAny(attribution)) return attribution;

    var elements = doc.querySelectorAll('[data-utm-passthrough]');
    elements.forEach(function (el) {
      var tag = el.tagName;
      if (tag === 'FORM') {
        Object.keys(attribution).forEach(function (key) {
          var input = el.querySelector('input[name="' + key + '"]');
          if (!input) {
            input = doc.createElement('input');
            input.type = 'hidden';
            input.name = key;
            el.appendChild(input);
          }
          input.value = attribution[key];
        });
      } else if (tag === 'A' && el.getAttribute('href')) {
        el.setAttribute('href', appendParamsToUrl(el.getAttribute('href'), attribution, loc.href));
      }
    });
    return attribution;
  }

  function autoRun() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    var run = function () {
      applyToDocument(document, window.localStorage, window.sessionStorage, window.location);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  // Im Browser automatisch starten
  if (typeof window !== 'undefined') {
    autoRun();
  }

  return {
    UTM_KEYS: UTM_KEYS,
    BACKGROUND_MAX_AGE_MS: BACKGROUND_MAX_AGE_MS,
    isExpired: isExpired,
    parseIncomingUTM: parseIncomingUTM,
    capture: capture,
    getAttribution: getAttribution,
    getSessionAttribution: getSessionAttribution,
    clearAttribution: clearAttribution,
    clearSession: clearSession,
    appendParamsToUrl: appendParamsToUrl,
    applyToDocument: applyToDocument,
    autoRun: autoRun
  };
}));
